import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Card, CardActionArea, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

type Props = {
  icon: ReactNode;
  title: string;
  description: string;
  isDisabled: boolean;
  onClick: () => void;
};

// The whole card is the button: picking a type is the step's only action, so there is
// nothing to confirm afterwards and no separate hit target to aim at.
export const SupportReportTypeCard = ({
  icon,
  title,
  description,
  isDisabled,
  onClick,
}: Props) => (
  <Card
    variant="outlined"
    sx={({ transitions, palette }) => ({
      width: "100%",
      borderRadius: 2,
      transition: transitions.create(["border-color", "box-shadow"]),
      ...(!isDisabled && {
        "&:hover": {
          borderColor: "primary.main",
          // An inset ring rather than a 2px border, so hovering a card doesn't resize it.
          boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
        },
      }),
      ...(isDisabled && { opacity: 0.5, filter: "grayscale(100%)" }),
    })}
  >
    <CardActionArea
      disabled={isDisabled}
      onClick={onClick}
      sx={{ p: 3, borderRadius: 2 }}
    >
      <Stack direction="row" alignItems="center" gap={3}>
        <Stack
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          width={48}
          height={48}
          borderRadius={2}
          bgcolor="grey.100"
          color="text.primary"
        >
          {icon}
        </Stack>
        <Stack gap={0.5} minWidth={0} flexGrow={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Stack>
        <ChevronRightIcon sx={{ flexShrink: 0, color: "text.disabled" }} />
      </Stack>
    </CardActionArea>
  </Card>
);
